'use client';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Input,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stack,
  useDisclosure,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Spinner,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useUser } from '~/hooks/useUser';
import { createClient } from '~/lib/providers/supabase/client';
import { MAX_MESSAGE_RECIPIENTS } from '~/constants';
import { API_PATHS, SPECULATOR_MATCH_SCORE_MIN } from '@habity/constants';
import { ICONS } from '~/constants/icons';
import {
  useEmailRecipients,
  useHomePreview,
  useRecentNotifications,
} from './useSendMessageModalData';
import { NotificationHistory } from './NotificationHistory';
import { ConfirmSendDialog } from './ConfirmSendDialog';

interface SendMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryParams: string;
  buyerCount: number;
  buyerIds?: number[];
  homeId?: number;
  defaultTitle?: string;
  defaultMessage?: string;
}

export function SendMessageModal({
  isOpen,
  onClose,
  queryParams,
  buyerCount,
  buyerIds,
  homeId,
  defaultTitle = '',
  defaultMessage = '',
}: SendMessageModalProps) {
  const [header, setHeader] = useState(defaultTitle);
  const [message, setMessage] = useState(defaultMessage);
  const [isSending, setIsSending] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<'personal' | 'matching'>(
    'personal'
  );
  const [minMatchScore, setMinMatchScore] = useState(
    SPECULATOR_MATCH_SCORE_MIN
  );
  const [tabIndex, setTabIndex] = useState(0);
  const prevOpenRef = useRef(false);
  const prevHomeIdRef = useRef<number | undefined>(undefined);
  const channels = ['email'];
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const { user } = useUser();
  const supabase = createClient();

  const { emailRecipientIds, isLoadingRecipients, maxMatchScoreInSearch } =
    useEmailRecipients(isOpen, queryParams, minMatchScore);
  const userDetails = useMemo(
    () =>
      user != null
        ? {
            firstName: user.first_name ?? undefined,
            lastName: user.last_name ?? undefined,
            email: user.email ?? undefined,
            phone: user.phone ?? undefined,
          }
        : null,
    [user?.id, user?.first_name, user?.last_name, user?.email, user?.phone]
  );
  useHomePreview(
    isOpen,
    homeId,
    emailTemplate,
    setMessage,
    setHeader,
    userDetails,
    defaultTitle,
    defaultMessage
  );
  const { recentNotifications, isLoadingHistory, fetchRecentNotifications } =
    useRecentNotifications(isOpen);

  const recipientCount =
    emailRecipientIds === null ? buyerCount : emailRecipientIds.length;

  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    if (justOpened) {
      setHeader(defaultTitle);
      setMessage(defaultMessage);
      setMinMatchScore(SPECULATOR_MATCH_SCORE_MIN);
      setTabIndex(0);
      prevHomeIdRef.current = homeId;
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, defaultTitle, defaultMessage, homeId]);

  // When user changes selected address while modal is open, reset to new default so useHomePreview re-applies for the new home
  useEffect(() => {
    if (!isOpen) return;
    const homeIdChanged =
      prevHomeIdRef.current !== undefined && prevHomeIdRef.current !== homeId;
    if (homeIdChanged) {
      setHeader(defaultTitle);
      setMessage(defaultMessage);
    }
    prevHomeIdRef.current = homeId;
  }, [isOpen, homeId, defaultTitle, defaultMessage]);

  const handleSend = () => {
    if (emailTemplate === 'personal') {
      if (!header.trim()) {
        toast({
          title: 'Rubrik krävs',
          description: 'Ange en rubrik för meddelandet',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!message.trim()) {
        toast({
          title: 'Meddelande krävs',
          description: 'Du måste skriva ett meddelande',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    if (emailTemplate === 'matching') {
      if (!homeId) {
        toast({
          title: 'Bostad krävs',
          description: 'Matchningsmail kräver att en bostad är vald',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!header.trim() || !message.trim()) {
        toast({
          title: 'Rubrik och meddelande krävs',
          description:
            'Ange både rubrik och ett kort meddelande för matchningsmailet',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    if (!user?.id) {
      toast({
        title: 'Fel',
        description: 'Kunde inte identifiera användare',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onConfirmOpen();
  };

  const handleSendTest = async () => {
    if (!user?.id) {
      toast({
        title: 'Fel',
        description: 'Kunde inte identifiera användare',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (emailTemplate === 'matching') {
      if (!homeId) {
        toast({
          title: 'Bostad krävs',
          description: 'Matchningsmail kräver att en bostad är vald',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!header.trim() || !message.trim()) {
        toast({
          title: 'Rubrik och meddelande krävs',
          description:
            'Ange både rubrik och ett kort meddelande för testmailet',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    if (emailTemplate === 'personal') {
      if (!header.trim()) {
        toast({
          title: 'Rubrik krävs',
          description: 'Ange en rubrik för att skicka testmail',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!message.trim()) {
        toast({
          title: 'Meddelande krävs',
          description:
            'Skriv ett meddelande för att skicka personligt testmail',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    setIsSending(true);
    try {
      const isMatching = emailTemplate === 'matching';
      const notificationType = isMatching
        ? 'speculator_matching'
        : 'speculator_personal';
      const title =
        header.trim() ||
        (isMatching ? 'Matchningsmail' : 'Meddelande till spekulanter');
      const bodyMessage = message.trim();

      const { error: testError } = await supabase
        .from('notifications')
        .insert({
          title,
          message: bodyMessage,
          sender_id: user.id,
          user_ids: [user.id],
          channels: ['email'],
          notification_type: notificationType,
          query_params: queryParams,
          recipient_count: 1,
          scheduled_at: new Date().toISOString(),
          is_sent: false,
          home_id: homeId || null,
        })
        .select()
        .single();

      if (testError) throw new Error(testError.message);

      toast({
        title: 'Testmail skickat',
        description: `Ett testmail skickas till ${
          user.email ?? 'din e-post'
        } inom kort`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      fetchRecentNotifications();
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Kunde inte skicka testmail',
        description: error instanceof Error ? error.message : 'Ett fel uppstod',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!user?.id) {
      toast({
        title: 'Fel',
        description: 'Kunde inte identifiera användare',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      onConfirmClose();
      return;
    }

    onConfirmClose();
    setIsSending(true);

    try {
      const baseIds = emailRecipientIds ?? [];
      let recipients: number[] =
        buyerIds && buyerIds.length > 0
          ? buyerIds.filter((id) => baseIds.includes(id))
          : [...baseIds];

      if (recipients.length === 0) {
        toast({
          title: 'Inga spekulanter att skicka till',
          description:
            buyerIds && buyerIds.length > 0 && baseIds.length === 0
              ? 'Inga spekulanter i sökningen når matchtröskeln (75 %).'
              : 'Inga spekulanter med tillräcklig matchning hittades.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (recipients.length > MAX_MESSAGE_RECIPIENTS) {
        recipients = recipients.slice(0, MAX_MESSAGE_RECIPIENTS);
      }

      const isMatching = emailTemplate === 'matching';
      const notificationType = isMatching
        ? 'speculator_matching'
        : 'speculator_personal';
      const title =
        header.trim() ||
        (isMatching ? 'Matchningsmail' : 'Meddelande till spekulanter');
      const bodyMessage = message.trim();

      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          title,
          message: bodyMessage,
          sender_id: user.id,
          user_ids: recipients,
          channels: channels,
          notification_type: notificationType,
          query_params: queryParams,
          recipient_count: recipients.length,
          scheduled_at: new Date().toISOString(),
          is_sent: false,
          home_id: homeId || null,
        })
        .select()
        .single();

      if (notificationError) {
        throw new Error(
          `Failed to create notification: ${notificationError.message}`
        );
      }

      const channelText =
        channels.includes('sms') && channels.includes('email')
          ? 'SMS och e-post'
          : channels.includes('sms')
          ? 'SMS'
          : 'e-post';

      toast({
        title: 'Meddelande schemalagt',
        description: `Meddelandet kommer att skickas via ${channelText} till ${recipients.length} spekulanter inom kort`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setHeader('');
      setMessage('');
      fetchRecentNotifications();
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Fel vid skickning',
        description:
          error instanceof Error
            ? error.message
            : 'Kunde inte skicka meddelande',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {tabIndex === 0 ? (
            <>
              {isLoadingRecipients
                ? 'Laddar…'
                : recipientCount === 0
                ? 'Inga matchande spekulanter'
                : `Tipsa ${recipientCount} spekulanter`}
              {emailRecipientIds !== null && recipientCount > 0 && (
                <Text
                  as="span"
                  fontWeight="normal"
                  fontSize="sm"
                  color="gray.600"
                >
                  {' '}
                  (≥{minMatchScore}%)
                </Text>
              )}
            </>
          ) : (
            'Meddelandehistorik'
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs variant="line" index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab>E-post</Tab>
              <Tab>Historik</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={4}>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>
                      Gränsvärde för matchning: <Box as="span" fontWeight="bold">{minMatchScore}%</Box>
                    </FormLabel>
                    <Stack py="0" px="0" spacing="0">
                      <Slider
                        colorScheme="blackAlpha"
                        aria-label="Minsta matchscore"
                        min={50}
                        max={100}
                        step={1}
                        value={minMatchScore}
                        onChange={setMinMatchScore}
                        height="40px"
                        mb="0px"
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb
                          w="8"
                          h="8"
                          borderWidth="1px"
                          borderColor="blackAlpha.600"
                          boxShadow="none"
                        >
                          <Box
                            color="blackAlpha.900"
                            w="4"
                            h="4"
                            as={ICONS.HOME}
                          />
                        </SliderThumb>
                      </Slider>
                      <HStack
                        justifyContent="space-between"
                        mt="0"
                        py="0"
                        px="0"
                      >
                        <Text size="xs">50%</Text>
                        <Text size="xs">100%</Text>
                      </HStack>
                    </Stack>
                  </FormControl>

                  {!isLoadingRecipients && (
                    <Alert
                      status={recipientCount === 0 ? 'warning' : 'success'}
                      borderRadius="md"
                      fontSize="xs"
                    >
                      <AlertIcon boxSize="4" />
                      <AlertDescription lineHeight="short">
                        {recipientCount === 0 ? (
                          <>
                            Inga spekulanter når matchtröskeln ({minMatchScore}{' '}
                            %) i denna sökning.
                            {maxMatchScoreInSearch != null && (
                              <>
                                {' '}
                                Sänk nivån till {maxMatchScoreInSearch} % eller
                                lägre för att inkludera spekulanter.
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {recipientCount} spekulanter når matchtröskeln (
                            {minMatchScore} %) i denna sökning.
                          </>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>Mall</FormLabel>
                      <VStack align="stretch" spacing={2}>
                        <HStack
                          p={3}
                          w="full"
                          borderWidth={1}
                          borderRadius="md"
                          borderColor={
                            emailTemplate === 'personal'
                              ? 'blue.500'
                              : 'gray.200'
                          }
                          bg={
                            emailTemplate === 'personal' ? 'blue.50' : 'white'
                          }
                          cursor="pointer"
                          onClick={() => setEmailTemplate('personal')}
                          _hover={{ borderColor: 'blue.300' }}
                        >
                          <Box
                            w={4}
                            h={4}
                            borderRadius="full"
                            borderWidth={2}
                            borderColor={
                              emailTemplate === 'personal'
                                ? 'blue.500'
                                : 'gray.300'
                            }
                            bg={
                              emailTemplate === 'personal'
                                ? 'blue.500'
                                : 'white'
                            }
                          />
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium">
                              Personligt meddelande
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Ett genuint, informellt mail direkt från mäklarens
                              mailadress med en bild på bostaden som bilaga.
                            </Text>
                          </VStack>
                        </HStack>
                        <HStack
                          p={3}
                          w="full"
                          borderWidth={1}
                          borderRadius="md"
                          borderColor={
                            emailTemplate === 'matching'
                              ? 'blue.500'
                              : 'gray.200'
                          }
                          bg={
                            emailTemplate === 'matching' ? 'blue.50' : 'white'
                          }
                          cursor={homeId ? 'pointer' : 'not-allowed'}
                          opacity={homeId ? 1 : 0.6}
                          onClick={() => homeId && setEmailTemplate('matching')}
                          _hover={
                            homeId ? { borderColor: 'blue.300' } : undefined
                          }
                        >
                          <Box
                            w={4}
                            h={4}
                            borderRadius="full"
                            borderWidth={2}
                            borderColor={
                              emailTemplate === 'matching'
                                ? 'blue.500'
                                : 'gray.300'
                            }
                            bg={
                              emailTemplate === 'matching'
                                ? 'blue.500'
                                : 'white'
                            }
                          />
                          <VStack align="start" spacing={1} flex={1}>
                            <Text fontWeight="medium">Matchningsmail</Text>
                            <Text fontSize="xs" color="gray.500">
                              Ett designat utskick med bild och information om
                              bostaden, samma mall som i den automatiska
                              matchningsutskicket.
                            </Text>
                            {!homeId && (
                              <Text fontSize="xs" color="gray.500">
                                Kräver att en publicerad bostad är vald.
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </VStack>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Rubrik</FormLabel>
                      <Input
                        value={header}
                        onChange={(e) => setHeader(e.target.value)}
                        placeholder={
                          emailTemplate === 'matching'
                            ? 'T.ex. Linnégatan 12 matchar din bevakning'
                            : 'T.ex. Ny bostad som matchar din sökning'
                        }
                        maxLength={100}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {header.length}/100 tecken
                      </Text>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Meddelande</FormLabel>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={
                          emailTemplate === 'matching'
                            ? 'Kort meddelande som visas i mailet'
                            : 'Skriv ditt meddelande här...'
                        }
                        rows={emailTemplate === 'matching' ? 3 : 6}
                        resize="vertical"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {message.length} tecken
                      </Text>
                    </FormControl>
                  </VStack>
                </VStack>
              </TabPanel>
              <TabPanel px={0} pt={4}>
                {isLoadingHistory ? (
                  <HStack justify="center" py={8}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">
                      Laddar historik...
                    </Text>
                  </HStack>
                ) : recentNotifications.length === 0 ? (
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    py={6}
                    textAlign="center"
                  >
                    Inga tidigare meddelanden
                  </Text>
                ) : (
                  <NotificationHistory
                    recentNotifications={recentNotifications}
                    isLoadingHistory={isLoadingHistory}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={tabIndex === 0 ? 3 : 0}
            onClick={onClose}
            isDisabled={isSending}
          >
            Avbryt
          </Button>
          {tabIndex === 0 && (
            <>
              <Button
                variant="outline"
                mr={3}
                onClick={handleSendTest}
                isLoading={isSending}
                isDisabled={
                  isSending ||
                  isLoadingRecipients ||
                  (emailTemplate === 'matching' &&
                    (!homeId || !header.trim() || !message.trim())) ||
                  (emailTemplate === 'personal' &&
                    (!header.trim() || !message.trim()))
                }
                loadingText="Skickar..."
              >
                Testa utskick
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSend}
                isLoading={isSending}
                isDisabled={
                  isSending ||
                  isLoadingRecipients ||
                  recipientCount === 0 ||
                  (emailTemplate === 'matching' &&
                    (!homeId || !header.trim() || !message.trim())) ||
                  (emailTemplate === 'personal' &&
                    (!header.trim() || !message.trim()))
                }
                loadingText="Skickar..."
              >
                Skicka
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>

      <ConfirmSendDialog
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        cancelRef={cancelRef}
        onConfirm={handleConfirmSend}
        isSending={isSending}
        emailTemplate={emailTemplate}
        recipientCount={recipientCount}
        channels={channels}
        header={header}
        message={message}
      />
    </Modal>
  );
}
